import React, { memo, useCallback, useState } from 'react';

import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { EMPTY_COMMENT_TEMPLATES } from 'constants/cicReason';

import {
  TEMPLATE_PICKER_LABEL,
  TEMPLATE_PICKER_PLACEHOLDER,
} from './CicCommentTemplatePicker.constants';
import { cicCommentTemplatesPropTypes } from './CicCommentTemplatePicker.propTypes';
import { templatePickerStyles } from './CicCommentTemplatePicker.styles';

/**
 * "Apply Comment Template" picker for the comment of a CIC reason. Picking a template hands its
 * text to `onApply`; the comment stays editable. Hidden when the reason has no templates, and the
 * picked template is cleared whenever `reasonId` changes.
 */
const CicCommentTemplatePicker = ({
  name,
  dataTestId,
  dataTestIdInput,
  dataTestIdOption,
  onApply,
  reasonId = null,
  commentTemplates = EMPTY_COMMENT_TEMPLATES,
  isDisabled = false,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedForReasonId, setSelectedForReasonId] = useState(reasonId);

  if (selectedForReasonId !== reasonId) {
    setSelectedForReasonId(reasonId);
    setSelectedTemplate(null);
  }

  const templates = commentTemplates ?? EMPTY_COMMENT_TEMPLATES;

  const onTemplateChange = useCallback(
    (e, option) => {
      setSelectedTemplate(option ?? null);

      if (option?.text) {
        onApply(option.text);
      }
    },
    [onApply],
  );

  return (
    <AutocompleteCDS
      withSortedOptions
      dataTestId={dataTestId}
      dataTestIdInput={dataTestIdInput}
      dataTestIdOption={dataTestIdOption}
      disabled={isDisabled}
      isHidden={_.isEmpty(templates)}
      label={TEMPLATE_PICKER_LABEL}
      name={name}
      optionKey="cicFeeReasonCommentTemplateId"
      optionName="text"
      options={templates}
      placeholder={TEMPLATE_PICKER_PLACEHOLDER}
      value={selectedTemplate}
      variant="outlined"
      width={templatePickerStyles.width}
      onChange={onTemplateChange}
    />
  );
};

CicCommentTemplatePicker.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  dataTestIdInput: PropTypes.string.isRequired,
  dataTestIdOption: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  commentTemplates: cicCommentTemplatesPropTypes,
  isDisabled: PropTypes.bool,
  reasonId: PropTypes.string,
};

export default memo(CicCommentTemplatePicker);
